import {
  EventListenerCallback,
  EventMapCore,
  ParamListBase,
  useFocusEffect,
  useNavigation,
} from '@react-navigation/native';
import {useCallback, useEffect, useRef} from 'react';
import {BackHandler, Platform} from 'react-native';
import {useSelector} from 'react-redux';
import {useAppDispatch} from '../../redux';
import {loginStepSlice} from '../../redux/reducers/runtime/loginStep';
import {LoginStepSelector} from '../../redux/selectors/runtime/loginStep';
import {Screens} from '../routing';
import {loginSlice} from '../../redux/reducers/login';
import {LoginState} from '../models/login/loginStep';
import {NavigationRoute} from '../components/tabBar/components/types';

export function useCustomBackhandler(disableBackSwipe?: boolean) {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const step = useSelector(LoginStepSelector());
  const isListenerAdded = useRef(false);
  const nowhereToGo = () => {
    if (step === LoginState.esiaLogin || step === LoginState.esiaRegCardUnconfirmed) {
      dispatch(loginSlice.actions.resetEsiaFlow(null));
    } else {
      dispatch(loginSlice.actions.reset(null));
    }
    dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
    navigation.navigate(Screens.Login);
  };
  const goBackAndLowerStepByOne = () => {
    if (disableBackSwipe) return true;
    if (step === LoginState.esiaLogin || step === LoginState.esiaRegCardUnconfirmed) {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        nowhereToGo();
      }
      return true;
    }
    if (step > LoginState.enter) {
      if (navigation.canGoBack()) {
        navigation.goBack();
        dispatch(loginStepSlice.actions.setLoginStep(step - 1));
      } else {
        nowhereToGo();
      }
    } else {
      nowhereToGo();
    }
    return true;
  };
  useEffect(() => {
    if (Platform.OS !== 'android' || disableBackSwipe) {
      return;
    }
    const customBackHandler = BackHandler.addEventListener('hardwareBackPress', goBackAndLowerStepByOne);
    return () => customBackHandler.remove();
  }, [disableBackSwipe, step]);

  useFocusEffect(
    useCallback(() => {
      if (disableBackSwipe || Platform.OS !== 'ios') {
        return;
      }

      let isHandled = false;
      let isNavigating = false;
      const onBeforeRemove: EventListenerCallback<
        EventMapCore<
          Readonly<{
            key: string;
            index: number;
            routeNames: string[];
            history?: unknown[];
            routes: NavigationRoute<ParamListBase, string>[];
            type: string;
            stale: false;
          }>
        >,
        'beforeRemove'
      > = (e) => {
        if (isNavigating || isHandled) {
          return;
        }
        e.preventDefault();
        isHandled = true;
        isNavigating = true;
        goBackAndLowerStepByOne();
        setTimeout(() => {
          isNavigating = false;
        }, 250);
      };

      isListenerAdded.current = true;
      navigation.addListener('beforeRemove', onBeforeRemove);

      return () => {
        isListenerAdded.current = false;
        navigation.removeListener('beforeRemove', onBeforeRemove);
      };
    }, [disableBackSwipe, navigation, step]),
  );
}
